import React from 'react'
import { UserStream } from 'components/filters/FilterIcon'
import CommonEditor from '../shared/CommonEditor'
import UserName from '../users/UserName'
import cn from 'classnames'
import styles from './post-answer.module.scss'
import { getImages, getText } from '../../lib/value'
import WithSelection from 'components/shared/WithSelection'
import AnswerTopControls from 'components/answers/AnswerTopControls'

export default class PostAnswer extends React.PureComponent {
    COLLAPSE_POST_LENGTH = 400

    constructor(props) {
        super(props)
        this.state = {
            showKnovigator: false,
        }
        this.richEditor = React.createRef()
        this.handleMoreClick = this.handleMoreClick.bind(this)

        this.instanceId = `post-answer-${
            this.props.answer?.id || Math.random().toString(36).substring(2, 9)
        }`
    }

    // Add a click handler method for the more button
    handleMoreClick() {
        if (this.props.setTextIsCollapsed) {
            this.props.setTextIsCollapsed(false)
        }
    }

    isUserSignedIn() {
        return document.getElementById('knovigator-body').getAttribute('data-user') === 'true'
    }

    voteContainerParent = React.createRef()

    // Methods to get content from uncontrolled editor
    getEditor = () => this.richEditor.current?.getEditor?.()
    getDeltaJson = () => this.getEditor()?.getContents()
    getContent = () => getText(this.getDeltaJson() || {}).trim()

    componentDidMount() {
        // Store a reference to the bound handler
        this.boundMoreButtonHandler = this.handleMoreButtonClicked.bind(this)

        // Add event listener directly on document for better cross-view support
        document.addEventListener('more-button-clicked', this.boundMoreButtonHandler)
    }

    componentWillUnmount() {
        // Remove event listener when component unmounts
        document.removeEventListener('more-button-clicked', this.boundMoreButtonHandler)
    }

    handleMoreButtonClicked = event => {
        // Only handle the event if it's for this specific instance
        if (event.detail.instanceId === this.instanceId && this.props.setTextIsCollapsed) {
            // Since the event is coming from document, we'll handle it immediately
            this.props.setTextIsCollapsed(false)

            // Return false to prevent any other handlers
            return false
        }
    }

    /* truncate a quill delta object to only return number of chars specified */
    truncateDelta(delta, maxChars = this.COLLAPSE_POST_LENGTH) {
        let charCount = 0
        let truncatedDelta = { ops: [] }

        for (const op of delta.ops) {
            if (charCount >= maxChars) break

            if (typeof op.insert === 'string') {
                let remainingChars = maxChars - charCount
                let text = op.insert.slice(0, remainingChars)
                charCount += text.length

                truncatedDelta.ops.push({ ...op, insert: text })

                if (charCount === maxChars) {
                    // Add ellipsis with standard text formatting
                    truncatedDelta.ops.push({ insert: '... ' })

                    // Only add the more button if this is not a reply preview
                    if (!this.props.isReplyPreview) {
                        // Add the more-button with an instance identifier
                        truncatedDelta.ops.push({
                            insert: {
                                'more-button': {
                                    id: 'expand-button',
                                    instanceId: this.instanceId,
                                },
                            },
                        })

                        // Add a space after more button for better touch target separation
                        truncatedDelta.ops.push({ insert: ' ' })
                    }
                }
            } else {
                truncatedDelta.ops.push(op)
            }
        }

        return truncatedDelta
    }

    render() {
        const { answer, textIsCollapsed, setTextIsCollapsed, COLLAPSE_POST_LENGTH } = this.props
        const isEmbed = this.props.isEmbed
        //console.log('RENDER POST ANSWER', answer.id, answer.delta_json)
        const showContent =
            this.props.editMode ||
            getText(answer.delta_json) ||
            getImages(answer.delta_json) ||
            (isEmbed && (getText(answer.nested_delta_json) || getImages(answer.nested_delta_json)))

        const embedPostContainerStyles = isEmbed ? styles.embedPostContainer : null
        const topPadding = isEmbed ? styles.embedPadding : null
        const showUser = !this.props.blog && !this.props.isStory

        const stylesUserIcon = cn(this.props.isTitle ? styles.titleUserIcon : styles.userIcon)

        const isAgent = answer.message_type === 'chatgpt' || answer.message_type === 'agent'

        const truncatedValue =
            this.props.value &&
            (this.props.isReplyPreview ||
                this.props.isEmbed ||
                (textIsCollapsed && !this.props.editMode))
                ? this.truncateDelta(
                      this.props.value,
                      this.props.isReplyPreview && this.props.maxCharacters
                          ? this.props.maxCharacters
                          : COLLAPSE_POST_LENGTH,
                  )
                : this.props.value

        const showCollapseButton =
            getText(this.props.value)?.length > COLLAPSE_POST_LENGTH && !this.props.editMode

        const systemUser = this.props.systemUser
        const isStory = this.props.isStory
        const isIndentLayout = this.props.isIndentLayout
        const isTitle = this.props.isTitle

        const userHasParagraphFeature = this.props.userHasParagraphFeature
        const newUserReplying = this.props.newUserReplying

        const hasParagraphStyles = userHasParagraphFeature && !isTitle && !newUserReplying

        return (
            <div className={cn(styles.postAnswerComp, topPadding)}>
                <div className={cn(styles.flexContainer)}>
                    <div className={cn(styles.postContainer, embedPostContainerStyles)}>
                        <div className={cn(styles.userAndControlsContainer)}>
                            {showUser && (
                                <div className={styles.userContainer}>
                                    <div
                                        className={cn(
                                            styles.userIconContainer,
                                            isIndentLayout && styles.indentUserIconContainer,
                                            isTitle && styles.titleUserIconContainer,
                                        )}
                                    >
                                        <UserStream
                                            filter={this.props.filter}
                                            panel={this.props.panel}
                                            user={answer.user}
                                            agentIcon={isAgent}
                                            virtualUserIcon={answer.virtual_user_icon_url}
                                            showKnovigator={this.props.showKnovigator}
                                            clickable={!isAgent}
                                            contextStyles={cn(
                                                styles.userIcon,
                                                this.props.isTitle && styles.titleUserIcon,
                                                isAgent && styles.agentIcon,
                                                isAgent && isIndentLayout && styles.indentAgentIcon,
                                                hasParagraphStyles && styles.paragraphIcon,
                                            )}
                                        />
                                    </div>

                                    <div
                                        className={cn(
                                            styles.userNameContainer,
                                            hasParagraphStyles && styles.userNameContainerParagraph,
                                        )}
                                    >
                                        <UserName
                                            user={answer.user}
                                            answer={answer}
                                            virtualUserName={answer.virtual_user_name}
                                            transcriptSegmentInfo={answer?.transcript?.segment_info}
                                            answerCreatedAt={answer.created_at}
                                            contextStyles={cn(
                                                styles.userName,
                                                hasParagraphStyles && styles.paragraph,
                                                this.props.isTitle && styles.titleUserName,
                                            )}
                                            showKnovigator={this.props.showKnovigator}
                                            agentModel={
                                                answer.message_type === 'agent'
                                                    ? answer.agent_model
                                                    : null
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            {!isStory && !systemUser && !isEmbed && (
                                <div
                                    className={cn(
                                        styles.answerTopControlsContainer,
                                        this.props.isTitle &&
                                            styles.titleAnswerTopControlsContainer,
                                    )}
                                >
                                    <AnswerTopControls
                                        isTitle={this.props.isTitle}
                                        isBsv={this.props.isBsv}
                                        answer={this.props.answer}
                                        quest={this.props.quest}
                                        hover={this.props.answerHover}
                                        toggleEdit={this.props.toggleEdit}
                                        showControlsPopup={this.props.showControlsPopup}
                                        toggleControlsPopup={this.props.toggleControlsPopup}
                                        archiveConfirm={this.props.archiveConfirm}
                                        toggleArchiveConfirm={this.props.toggleArchiveConfirm}
                                        getPublicLink={this.props.getPublicLink}
                                        isUserSignedIn={this.props.isUserSignedIn}
                                        editMode={this.props.editMode}
                                    />
                                </div>
                            )}
                        </div>

                        {showContent && (
                            <div
                                className={cn(
                                    styles.contentContainer,
                                    isIndentLayout && styles.indentContentContainer,
                                    this.props.isTitle && styles.titleContentContainer,
                                    this.props.editMode && styles.editContentContainer,
                                    this.props.isReplyPreview && styles.replyPreviewContent,
                                )}
                            >
                                <WithSelection editMode={this.props.editMode}>
                                    <CommonEditor
                                        ref={this.richEditor}
                                        isEmbed={isEmbed}
                                        type={this.props.isTitle ? 'title' : 'answer'}
                                        defaultValue={truncatedValue}
                                        placeholder="say something..."
                                        setEmbeds={this.props.setEmbeds}
                                        setLinkPreviews={this.props.setLinkPreviews}
                                        autoSave={true}
                                        postHandler={this.props.savePost}
                                        imgHandler={this.props.imgHandler}
                                        readOnly={!this.props.editMode}
                                        answer={this.props.answer}
                                        panel={this.props.panel}
                                        uiId={this.props.uiId}
                                        onEditorLoad={editor => {
                                            if (this.props.editMode) {
                                                // Position cursor at the end when entering edit mode
                                                const length = editor.getLength() || 0
                                                editor.setSelection(length, 0)
                                            }
                                        }}
                                    />
                                </WithSelection>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}
